"use client";

import React from "react";
import { MapPin, Phone, Mail, CircleCheck, Loader, ArrowRight, Factory } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const SHARED_DATA = {
  business_name: "Oriental World Corporation Limited",
  phone: "+852 2345 6789",
  email: "trade@orientalworld.hk",
  address: "Central, Hong Kong"
};
const STEPS = [];
function KitchenContactForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center space-y-4">
                <div className="size-16 bg-primary/10 text-primary rounded-md flex items-center justify-center mb-4">
                    <CircleCheck className="size-8" />
                </div>
                <h3 className="text-2xl font-default font-bold text-foreground">Inquiry Received</h3>
                <p className="text-muted-foreground max-w-md">
                    {successMessage}
                </p>
                <Button onClick={resetForm} variant="outline" className="mt-6 rounded-md border-primary text-primary">
                    Submit New Inquiry
                </Button>
            </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <FormField control={control} name="surname" rules={{
        required: "Surname is required"
      }} render={({
        field
      }) => <FormItem>
                            <FormLabel className="font-default font-semibold uppercase tracking-wider text-[11px] sm:text-xs">Surname</FormLabel>
                            <FormControl>
                                <Input placeholder="Enter your surname" className="h-10 sm:h-11 rounded-md border-input focus-visible:ring-primary/20" {...field} />
                            </FormControl>
                            <FormMessage />
                        </FormItem>} />

                <FormField control={control} name="firstName" rules={{
        required: "First name is required"
      }} render={({
        field
      }) => <FormItem>
                            <FormLabel className="font-default font-semibold uppercase tracking-wider text-[11px] sm:text-xs">First Name</FormLabel>
                            <FormControl>
                                <Input placeholder="Enter your first name" className="h-10 sm:h-11 rounded-md border-input focus-visible:ring-primary/20" {...field} />
                            </FormControl>
                            <FormMessage />
                        </FormItem>} />
            </div>

            <FormField control={control} name="company" rules={{
      required: "Company name is required"
    }} render={({
      field
    }) => <FormItem>
                        <FormLabel className="font-default font-semibold uppercase tracking-wider text-[11px] sm:text-xs">Company Name</FormLabel>
                        <FormControl>
                            <Input placeholder="Registered entity name" className="h-10 sm:h-11 rounded-md border-input focus-visible:ring-primary/20" {...field} />
                        </FormControl>
                        <FormMessage />
                    </FormItem>} />

            <FormField control={control} name="email" rules={{
      required: "Email is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Please enter a valid email address"
      }
    }} render={({
      field
    }) => <FormItem>
                        <FormLabel className="font-default font-semibold uppercase tracking-wider text-[11px] sm:text-xs">Email</FormLabel>
                        <FormControl>
                            <Input type="email" placeholder="name@company.com" className="h-10 sm:h-11 rounded-md border-input focus-visible:ring-primary/20" {...field} />
                        </FormControl>
                        <FormMessage />
                    </FormItem>} />

            <FormField control={control} name="areaCode" render={({
      field
    }) => <FormItem>
                        <FormLabel className="font-default font-semibold uppercase tracking-wider text-[11px] sm:text-xs">Phone Number (Optional)</FormLabel>
                        <FormControl>
                            <div className="flex flex-col sm:flex-row gap-2">
                                <Input className="h-10 sm:h-11 w-full sm:flex-1 rounded-md border border-input" placeholder="+852" {...field} />
                                <FormField control={control} name="phone" render={({
            field: phoneField
          }) => <Input placeholder="Phone number" className="h-10 sm:h-11 w-full sm:flex-1 rounded-md border border-input focus-visible:ring-primary/20" {...phoneField} />} />
                            </div>
                        </FormControl>
                        <FormMessage />
                    </FormItem>} />

            <FormField control={control} name="website" render={({
      field
    }) => <FormItem>
                        <FormLabel className="font-default font-semibold uppercase tracking-wider text-[11px] sm:text-xs">Website (Optional)</FormLabel>
                        <FormControl>
                            <Input placeholder="https://www.company.com" className="h-10 sm:h-11 rounded-md border-input focus-visible:ring-primary/20" {...field} />
                        </FormControl>
                        <FormMessage />
                    </FormItem>} />

            <FormField control={control} name="product" render={({
      field
    }) => <FormItem>
                        <FormLabel className="font-default font-semibold uppercase tracking-wider text-[11px] sm:text-xs">Product of Interest</FormLabel>
                        <FormControl>
                            <select className="flex h-10 sm:h-11 w-full items-center justify-between rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-xs transition-colors focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-primary/20 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm font-default" {...field}>
                                <option value="" disabled>Select a product</option>
                                {["Industrial Tallow", "Used Cooking Oil (UCO)", "Mixed Acid Oil (MAO)", "Plant-Based Oil", "Other"].map((opt, i) => <option key={i} value={opt} data-index={i}>{opt}</option>)}
                            </select>
                        </FormControl>
                        <FormMessage />
                    </FormItem>} />

            <FormField control={control} name="details" rules={{
      required: "Inquiry Details is required"
    }} render={({
      field
    }) => <FormItem>
                        <FormLabel className="font-default font-semibold uppercase tracking-wider text-[11px] sm:text-xs">Inquiry Details (Required)</FormLabel>
                        <FormControl>
                            <textarea className="flex min-h-[120px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-base shadow-xs placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-primary/20 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm resize-y font-default" placeholder="Please specify product requirements, desired volume, destination port, and preferred Incoterms." {...field} />
                        </FormControl>
                        <FormMessage />
                    </FormItem>} />

            {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-md">
                    <p className="text-sm text-destructive font-medium">{submitError}</p>
                </div>}

            <Button type="submit" disabled={isSubmitting} className="w-full h-10 sm:h-11 text-sm sm:text-base mt-2 bg-primary text-primary-foreground hover:bg-primary/90 rounded-md font-default font-bold uppercase tracking-widest">
                {isSubmitting ? <>
                        <Loader className="animate-spin mr-2 size-5" />
                        Processing...
                    </> : <>
                        Send Inquiry
                        <ArrowRight className="ml-2 size-4" />
                    </>}
            </Button>
        </Form>;
}
export default function LetsDesignYourKitchen() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="237798" id="trade-inquiries" className="bg-background text-foreground py-16 md:py-24 overflow-hidden" ref={sectionRef}>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className={`grid grid-cols-1 lg:grid-cols-12 gap-6 sm:gap-8 md:gap-10 lg:gap-8 items-start transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
                    
                    <div className="lg:col-span-5 space-y-10">
                        <div>
                            <h2 className="font-default text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-foreground tracking-tighter mb-4">CONTACT</h2>
                            <p className="text-base sm:text-lg text-muted-foreground leading-relaxed font-default">Contact us for industrial feedstock sourcing, logistics coordination, and regional trade inquiries.</p>
                        </div>

                        <div className="space-y-6">
                            <div className="flex items-start gap-4">
                                <div className="mt-1 bg-secondary p-3 rounded-md text-primary shrink-0">
                                    <Phone className="size-6" />
                                </div>
                                <div>
                                    <h4 className="font-default font-bold text-foreground text-lg uppercase tracking-tight">telephone</h4>
                                    <p className="text-muted-foreground mt-1 font-default">+852 9143 3495</p>
                                </div>
                            </div>
                            
                            <div className="flex items-start gap-4">
                                <div className="mt-1 bg-secondary p-3 rounded-md text-primary shrink-0">
                                    <Mail className="size-6" />
                                </div>
                                <div>
                                    <h4 className="font-default font-bold text-foreground text-lg uppercase tracking-tight">email</h4>
                                    <p className="text-muted-foreground mt-1 font-default">info@owcl.com.hk</p>
                                </div>
                            </div>

                            <div className="flex items-start gap-4">
                                <div className="mt-1 bg-secondary p-3 rounded-md text-primary shrink-0">
                                    <MapPin className="size-6" />
                                </div>
                                <div>
                                    <h4 className="font-default font-bold text-foreground text-lg uppercase tracking-tight">office&nbsp;</h4>
                                    <p className="text-muted-foreground mt-1 font-default">Flat 1, 5/F, Block P, Sunshine City, 8 On Shing Street, Ma On Shan, New Territories, Hong Kong SAR</p>
                                </div>
                            </div>

                            <div className="flex items-start gap-4">
                                <div className="mt-1 bg-secondary p-3 rounded-md text-primary shrink-0">
                                    <Factory className="size-6" />
                                </div>
                                <div>
                                    <h4 className="font-default font-bold text-foreground text-lg uppercase tracking-tight">Oil Plant</h4>
                                    <p className="text-muted-foreground mt-1 font-default">DD107, Lot 573-576, Fung Kat Heung, Yuen Long, New Territories, Hong Kong SAR</p>
                                </div>
                            </div>
                        </div>

                        <Separator className="my-8 bg-border" />

                        <div>
                            
                            <div className="space-y-6">
                                {STEPS.map((step, i) => <div key={i} data-index={i} className="flex gap-4">
                                        <div className="flex flex-col items-center">
                                            <div className="flex items-center justify-center size-8 rounded-md bg-primary text-primary-foreground font-bold text-sm shrink-0">
                                                {i + 1}
                                            </div>
                                            {i !== STEPS.length - 1 && <div className="w-px h-full bg-border mt-2" />}
                                        </div>
                                        <div className="pb-2">
                                            <h4 className="font-default font-bold text-foreground uppercase text-sm tracking-wide">{step.title}</h4>
                                            
                                        </div>
                                    </div>)}
                            </div>
                        </div>
                    </div>

                    <div className="lg:col-span-7 lg:pl-8">
                        <Card className="border-border bg-card shadow-md rounded-md">
                            <CardHeader className="pb-6 border-b border-border mb-6">
                                <CardTitle className="text-2xl font-default font-bold uppercase tracking-tight">Trade Inquiry Form</CardTitle>
                                
                            </CardHeader>
                            <CardContent>
                                <FormProvider formKey="trade_inquiry_form_contact" formId="trade_inquiry_form_contact" sectionName="TradeInquiries" defaultValues={{
                surname: "",
                firstName: "",
                company: "",
                email: "",
                areaCode: "",
                phone: "",
                website: "",
                product: "",
                details: ""
              }} submitText="Send Inquiry" successMessage="Your trade inquiry has been successfully transmitted to our Hong Kong desk. A regional representative will contact you shortly with technical specifications and logistics options.">
                                    <KitchenContactForm />
                                </FormProvider>
                            </CardContent>
                        </Card>
                    </div>

                </div>
            </div>
        </section>;
}